/**
 * `Mat2: Mat2DApi` — 2x3 affine matrices (W1-01 core-time-math).
 *
 * Layout [a, b, c, d, e, f] (CanvasRenderingContext2D order, rendering.md §10):
 *   x' = a*x + c*y + e
 *   y' = b*x + d*y + f
 * `multiply(A, B)` applies B first, then A. `translate` / `rotateDeg` / `scale` post-multiply like the canvas API
 * (`out = m · T`), i.e. the new operation acts in m's local space. Every `out` may alias an input, so pooled
 * scratch matrices can be reused in hot loops without allocation (`createMat2Pool`).
 */

import type { Mat2D, MutMat2D, Vec2 } from '../util/types';
import type { Mat2DApi } from './types';

function write(out: MutMat2D, a: number, b: number, c: number, d: number, e: number, f: number): MutMat2D {
  out[0] = a;
  out[1] = b;
  out[2] = c;
  out[3] = d;
  out[4] = e;
  out[5] = f;
  return out;
}

/**
 * cos/sin of a rotation in degrees, exact (0, +-1) for multiples of 90 degrees so that axis-aligned rotations
 * do not leave 6e-17 residues in matrices. The angle is first reduced modulo 360 (exact in floating point), so
 * large accumulated angles keep full precision in the conversion to radians.
 */
export function cosSinDeg(deg: number): readonly [cos: number, sin: number] {
  const r = deg % 360;
  if (r % 90 === 0) {
    const q = (((r / 90) % 4) + 4) % 4;
    return q === 0 ? [1, 0] : q === 1 ? [0, 1] : q === 2 ? [-1, 0] : [0, -1];
  }
  const rad = (r * Math.PI) / 180;
  return [Math.cos(rad), Math.sin(rad)];
}

export const Mat2: Mat2DApi = {
  identity(out) {
    return write(out ?? [0, 0, 0, 0, 0, 0], 1, 0, 0, 1, 0, 0);
  },
  multiply(A, B, out) {
    const [a0, a1, a2, a3, a4, a5] = A;
    const [b0, b1, b2, b3, b4, b5] = B;
    return write(
      out,
      a0 * b0 + a2 * b1,
      a1 * b0 + a3 * b1,
      a0 * b2 + a2 * b3,
      a1 * b2 + a3 * b3,
      a0 * b4 + a2 * b5 + a4,
      a1 * b4 + a3 * b5 + a5,
    );
  },
  invert(m, out) {
    const [a, b, c, d, e, f] = m;
    const det = a * d - b * c;
    if (det === 0 || !Number.isFinite(det)) return null;
    const inv = 1 / det;
    return write(out, d * inv, -b * inv, -c * inv, a * inv, (c * f - d * e) * inv, (b * e - a * f) * inv);
  },
  apply(m, x, y) {
    return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
  },
  translate(m, x, y, out) {
    const [a, b, c, d, e, f] = m;
    return write(out, a, b, c, d, a * x + c * y + e, b * x + d * y + f);
  },
  rotateDeg(m, deg, out) {
    const [cs, sn] = cosSinDeg(deg);
    const [a, b, c, d, e, f] = m;
    // m · [cs, sn, -sn, cs, 0, 0]
    return write(out, a * cs + c * sn, b * cs + d * sn, c * cs - a * sn, d * cs - b * sn, e, f);
  },
  scale(m, sx, sy, out) {
    const [a, b, c, d, e, f] = m;
    return write(out, a * sx, b * sx, c * sy, d * sy, e, f);
  },
};

// ---------------------------------------------------------------------------------------------------------------
// Additive helpers
// ---------------------------------------------------------------------------------------------------------------

/** A fresh identity matrix. */
export function mat2Identity(): MutMat2D {
  return [1, 0, 0, 1, 0, 0];
}

/** Copies m into out. */
export function mat2Copy(m: Mat2D, out: MutMat2D): MutMat2D {
  return write(out, m[0], m[1], m[2], m[3], m[4], m[5]);
}

/** Pure translation matrix. */
export function mat2FromTranslation(x: number, y: number, out?: MutMat2D): MutMat2D {
  return write(out ?? [0, 0, 0, 0, 0, 0], 1, 0, 0, 1, x, y);
}

/** Pure rotation matrix (counter-clockwise degrees, y-up). */
export function mat2FromRotationDeg(deg: number, out?: MutMat2D): MutMat2D {
  const [cs, sn] = cosSinDeg(deg);
  return write(out ?? [0, 0, 0, 0, 0, 0], cs, sn, -sn, cs, 0, 0);
}

/** Pure scale matrix. */
export function mat2FromScale(sx: number, sy: number, out?: MutMat2D): MutMat2D {
  return write(out ?? [0, 0, 0, 0, 0, 0], sx, 0, 0, sy, 0, 0);
}

/** Applies m to (x, y) writing into `out` (no allocation). */
export function mat2ApplyTo(m: Mat2D, x: number, y: number, out: [number, number]): [number, number] {
  out[0] = m[0] * x + m[2] * y + m[4];
  out[1] = m[1] * x + m[3] * y + m[5];
  return out;
}

/** Applies only the linear part (no translation): direction vectors. */
export function mat2ApplyLinear(m: Mat2D, x: number, y: number): Vec2 {
  return [m[0] * x + m[2] * y, m[1] * x + m[3] * y];
}

/** Element-wise equality within `eps` (default exact). */
export function mat2Equals(a: Mat2D, b: Mat2D, eps = 0): boolean {
  for (let i = 0; i < 6; i++) {
    if (!(Math.abs((a[i] ?? 0) - (b[i] ?? 0)) <= eps)) return false;
  }
  return true;
}

/** Determinant of the linear part. */
export function mat2Determinant(m: Mat2D): number {
  return m[0] * m[3] - m[1] * m[2];
}

/**
 * A pool of scratch matrices for hot loops: `acquire()` hands out pooled matrices (grown on demand) and
 * `reset()` returns them all at once (call it at the start of each frame).
 */
export interface Mat2Pool {
  acquire(): MutMat2D;
  reset(): void;
  /** Matrices currently handed out. */
  readonly used: number;
  /** Matrices allocated so far. */
  readonly capacity: number;
}

export function createMat2Pool(initial = 16): Mat2Pool {
  const items: MutMat2D[] = [];
  for (let i = 0; i < initial; i++) items.push(mat2Identity());
  let used = 0;
  return {
    acquire() {
      let m = items[used];
      if (m === undefined) {
        m = mat2Identity();
        items.push(m);
      }
      used += 1;
      return m;
    },
    reset() {
      used = 0;
    },
    get used() {
      return used;
    },
    get capacity() {
      return items.length;
    },
  };
}
